import { useClient } from '@cortex/react-query';
import type { WorkflowSchedule } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { useState, type CSSProperties } from 'react';

import { cronPresetLabel } from '../../../lib/cron-presets';
import { formatDateTime, formatRelative } from '../../../lib/format';
import {
  isPausedByWorkflow,
  isSkipNote,
  managedScheduleNote,
  pausedByWorkflowTitle,
  pausedScheduleLine,
  scheduleOwnershipNote,
  useWorkflowScheduleActions,
  workflowSchedulesKey,
} from '../../../lib/workflow-schedules';
import { EmptyState } from '../../common/EmptyState';
import { Spinner } from '../../common/Spinner';
import { StatusBadge } from '../../common/StatusBadge';
import { ScheduleTagChips } from '../ScheduleTags';
import { useWorkflowEditor } from '../WorkflowEditorContext';
import { WorkflowScheduleModal } from '../WorkflowScheduleModal';

import { ScheduledRunHistory } from './ScheduledRunHistory';

const listStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
  padding: 'var(--sp-3)',
};

const cardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
  padding: 'var(--sp-3)',
  background: 'var(--surface-2)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
};

const headerRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--sp-2)',
};

const metaGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'max-content 1fr',
  columnGap: 'var(--sp-3)',
  rowGap: 'var(--sp-1)',
  fontSize: 'var(--fs-xs)',
  color: 'var(--text-secondary)',
};

const actionsRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--sp-1)',
  justifyContent: 'flex-end',
};

/**
 * Right-panel list of this workflow's schedules (DEV-17470): cron + zone,
 * tags (DEV-17502; "Org-wide" when none),
 * next run, last run with its status (click-through to the Runs tab),
 * pause / resume, run now, edit, delete, then the scheduled run history
 * under the cards. Polls every 30 s to match the api tick so "Next run"
 * and "Last run" move without a reload.
 *
 * On a Formant-managed workflow (DEV-17471) the schedule is provisioned by
 * the platform: there is no create or delete, the edit form locks everything
 * but the cadence, and a pause the platform made says why.
 */
export function SchedulesPanel({ onOpenRun }: { onOpenRun: (runId: string) => void }) {
  const client = useClient();
  const { workflow } = useWorkflowEditor();
  const managed = !!workflow.managedBy;
  const key = workflowSchedulesKey(workflow.id);
  const [editing, setEditing] = useState<WorkflowSchedule | 'new' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const schedules = useQuery({
    queryKey: key,
    queryFn: () =>
      client.http.get(`v1/workflows/${workflow.id}/schedules`).json<WorkflowSchedule[]>(),
    refetchInterval: 30_000,
  });

  const actions = useWorkflowScheduleActions({
    onError: setActionError,
    onRunCreated: (run) => {
      setActionError(null);
      onOpenRun(run.id);
    },
  });

  const rows = schedules.data ?? [];

  return (
    <div style={listStyle}>
      <div style={headerRow}>
        <span className="t-mono-label">
          {rows.length} schedule{rows.length === 1 ? '' : 's'}
        </span>
        {managed ? null : (
          <button
            type="button"
            className="btn btn--primary btn--xs"
            onClick={() => setEditing('new')}
          >
            New schedule
          </button>
        )}
      </div>

      <p className="t-xs" style={{ color: 'var(--text-secondary)', margin: 0 }}>
        {managed ? managedScheduleNote : scheduleOwnershipNote}
      </p>

      {actionError ? (
        <div className="alert alert--danger" style={{ fontSize: 12 }}>
          {actionError}
        </div>
      ) : null}

      {schedules.isLoading ? (
        <Spinner />
      ) : schedules.error ? (
        <div className="alert alert--danger">
          Failed to load: {(schedules.error as Error).message}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No schedules"
          description={
            managed
              ? 'The schedule appears once Formant has provisioned this workflow.'
              : 'Run this workflow on a cron: every 15 minutes, daily at 09:00, weekly on Monday.'
          }
          cta={
            managed ? undefined : (
              <button
                type="button"
                className="btn btn--outline btn--sm"
                onClick={() => setEditing('new')}
              >
                Schedule this workflow
              </button>
            )
          }
        />
      ) : (
        rows.map((s) => (
          <ScheduleCard
            key={s.id}
            schedule={s}
            managed={managed}
            busy={actions.busy}
            onToggle={() =>
              actions.setEnabled.mutate({ workflowId: workflow.id, id: s.id, enabled: !s.enabled })
            }
            onRunNow={() => actions.runNow.mutate({ workflowId: workflow.id, id: s.id })}
            onEdit={() => setEditing(s)}
            onDelete={() => {
              if (window.confirm(`Delete schedule "${s.name}"?`)) {
                actions.remove.mutate({ workflowId: workflow.id, id: s.id });
              }
            }}
            onOpenRun={onOpenRun}
          />
        ))
      )}

      {!schedules.isLoading && !schedules.error ? (
        <ScheduledRunHistory workflowId={workflow.id} schedules={rows} onOpenRun={onOpenRun} />
      ) : null}

      <WorkflowScheduleModal
        open={editing !== null}
        workflowId={workflow.id}
        existing={editing === 'new' ? null : editing}
        managed={managed}
        onClose={() => setEditing(null)}
        onSaved={() => setEditing(null)}
      />
    </div>
  );
}

function ScheduleCard({
  schedule: s,
  managed,
  busy,
  onToggle,
  onRunNow,
  onEdit,
  onDelete,
  onOpenRun,
}: {
  schedule: WorkflowSchedule;
  managed: boolean;
  busy: boolean;
  onToggle: () => void;
  onRunNow: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onOpenRun: (runId: string) => void;
}) {
  const preset = cronPresetLabel(s.cron);
  // Null while enabled, and when the breaker's own sentence in lastError
  // already says why (a managed workflow): that line is not repeated here.
  const pausedCopy = pausedScheduleLine(s);
  // Paused because the workflow itself is disabled: the schedule cannot be
  // resumed or fired from here; enabling the workflow brings it back.
  const workflowOff = isPausedByWorkflow(s);
  return (
    <div style={cardStyle} data-testid="schedule-card">
      <div style={headerRow}>
        <span style={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-med)' }}>{s.name}</span>
        <span className={s.enabled ? 'badge badge--success' : 'badge badge--neutral'}>
          {s.enabled ? 'Active' : 'Paused'}
        </span>
      </div>

      {pausedCopy ? (
        <p className="t-xs t-muted" style={{ margin: 0 }} data-testid="schedule-paused">
          {pausedCopy}
        </p>
      ) : null}

      <div style={metaGrid}>
        <span className="t-mono-label">Cron</span>
        <span>
          <code className="t-mono">{s.cron}</code>
          {preset ? <span style={{ marginLeft: 'var(--sp-2)' }}>{preset}</span> : null}
          <span style={{ marginLeft: 'var(--sp-2)' }}>{s.timezone}</span>
        </span>

        <span className="t-mono-label">Tags</span>
        <span>
          <ScheduleTagChips tags={s.tags} />
        </span>

        <span className="t-mono-label">Next run</span>
        <span>
          {s.enabled && s.nextRunAt ? (
            <span title={formatDateTime(s.nextRunAt)}>
              {formatRelative(s.nextRunAt)} ({formatDateTime(s.nextRunAt)})
            </span>
          ) : (
            <span className="t-muted">Paused</span>
          )}
        </span>

        <span className="t-mono-label">Last run</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          {s.lastRunId ? (
            <>
              {s.lastRunStatus ? <StatusBadge status={s.lastRunStatus} /> : null}
              <button
                type="button"
                className="btn btn--ghost btn--xs"
                onClick={() => onOpenRun(s.lastRunId as string)}
                title={s.lastRunAt ? formatDateTime(s.lastRunAt) : undefined}
              >
                {s.lastRunAt ? formatRelative(s.lastRunAt) : 'open'}
              </button>
            </>
          ) : (
            <span className="t-muted">Never fired</span>
          )}
        </span>

        {s.lastError ? (
          <>
            <span className="t-mono-label">
              {isSkipNote(s.lastError) ? 'Last slot' : 'Last error'}
            </span>
            <span
              style={{ color: isSkipNote(s.lastError) ? undefined : 'var(--status-danger)' }}
              data-testid="schedule-last-error"
            >
              {s.lastError}
              {!isSkipNote(s.lastError) && s.consecutiveFailures > 0
                ? ` (${s.consecutiveFailures} in a row)`
                : ''}
            </span>
          </>
        ) : null}
      </div>

      <div style={actionsRow}>
        <button
          type="button"
          className="btn btn--outline btn--xs"
          onClick={onRunNow}
          disabled={busy || workflowOff}
          title={
            workflowOff ? pausedByWorkflowTitle : "Create a run now with this schedule's input"
          }
        >
          Run now
        </button>
        <button
          type="button"
          className="btn btn--outline btn--xs"
          onClick={onToggle}
          disabled={busy || workflowOff}
          title={workflowOff ? pausedByWorkflowTitle : undefined}
        >
          {s.enabled ? 'Pause' : 'Resume'}
        </button>
        <button type="button" className="btn btn--ghost btn--xs" onClick={onEdit}>
          Edit
        </button>
        {managed ? null : (
          <button
            type="button"
            className="btn btn--ghost btn--xs"
            onClick={onDelete}
            disabled={busy}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}
